import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";

import Home from "@/pages/Home";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TwoFactor from "@/pages/TwoFactor";
import PublicStats from "@/pages/PublicStats";
import AuditLog from "@/pages/AuditLog";
import CloseReasonsAdmin from "@/pages/CloseReasonsAdmin";
import RequestHelp from "@/pages/RequestHelp";
import Triage from "@/pages/Triage";
import CaseView from "@/pages/CaseView";
import MyCases from "@/pages/MyCases";
import Clinics from "@/pages/Clinics";
import ConsultantDashboard from "@/pages/ConsultantDashboard";
import ConsultantCase from "@/pages/ConsultantCase";
import AdminDashboard from "@/pages/AdminDashboard";
import FamilyPlan from "@/pages/FamilyPlan";
import Proposal from "@/pages/Proposal";
import { ApplyConsultor } from "@/pages/Apply";
import ProfessionalApply from "@/pages/ProfessionalApply";
import ClinicApply from "@/pages/ClinicApply";
import ProfessionalDashboard from "@/pages/ProfessionalDashboard";
import { ClinicDashboard, ClinicCase } from "@/pages/Clinic";
import ForcePasswordChange from "@/pages/ForcePasswordChange";
import PublicLookup from "@/pages/PublicLookup";
import PublicVerify from "@/pages/PublicVerify";
import PublicConsultor from "@/pages/PublicConsultor";
import AdminWarRoom from "@/pages/AdminWarRoom";
import UserProfile from "@/pages/UserProfile";
import ClinicProfileEdit from "@/pages/ClinicProfileEdit";
import PartnerRanking from "@/pages/PartnerRanking";
import PlantaoSchedule from "@/pages/PlantaoSchedule";
import Impact from "@/pages/Impact";
import { PublicClinics, PublicClinicDetail } from "@/pages/PublicClinics";
import PublicProfessionals from "@/pages/PublicProfessionals";
import PublicProfessionalDetail from "@/pages/PublicProfessionalDetail";
import BookingSuccess from "@/pages/BookingSuccess";
import BookingCancel from "@/pages/BookingCancel";
import ConsultationRoom from "@/pages/ConsultationRoom";
import Atendimento from "@/pages/Atendimento";
import PoliticaPrivacidade from "@/pages/PoliticaPrivacidade";
import PoliciesConsultation from "@/pages/PoliciesConsultation";
import TermosUso from "@/pages/TermosUso";
import TermoConfidencialidade from "@/pages/TermoConfidencialidade";
import PoliticaCookies from "@/pages/PoliticaCookies";
import MeusDados from "@/pages/MeusDados";
import AdminCentralAtendimento from "@/pages/AdminCentralAtendimento";
import ConsultorCentralAtendimento from "@/pages/ConsultorCentralAtendimento";
import Compliance from "@/pages/Compliance";
import FAQ from "@/pages/FAQ";
import PublicBlog from "@/pages/PublicBlog";
import PublicBlogPost from "@/pages/PublicBlogPost";
import PublicBlogSaved from "@/pages/PublicBlogSaved";
import AdminBlog from "@/pages/AdminBlog";
import AdminLgpd from "@/pages/AdminLgpd";
import AdminAjuda from "@/pages/AdminAjuda";
// Filho Presente — expansão para coordenação de cuidados de idosos
import FilhoPresenteHome from "@/pages/FilhoPresenteHome";
import FilhoPresenteDashboard from "@/pages/FilhoPresenteDashboard";
import FPBeneficiaryList from "@/pages/FPBeneficiaryList";
import FPBeneficiaryForm from "@/pages/FPBeneficiaryForm";
import FPBeneficiaryProfile from "@/pages/FPBeneficiaryProfile";
import FPProfessionals from "@/pages/FPProfessionals";
import FPProfessionalDetail from "@/pages/FPProfessionalDetail";
import FPRequestForm from "@/pages/FPRequestForm";
import { FPRequestList, FPRequestDetail } from "@/pages/FPRequests";
import FPAgenda from "@/pages/FPAgenda";
import AdminFilhoPresente from "@/pages/AdminFilhoPresente";
import FPComingSoon from "@/pages/FPComingSoon";
import FPRequestKindForm from "@/pages/FPRequestKindForm";
import FPCarePlan from "@/pages/FPCarePlan";
import FPDocuments from "@/pages/FPDocuments";
import FPProfessionalArea from "@/pages/FPProfessionalArea";
import ConsultantFPInbox from "@/pages/ConsultantFPInbox";
import FPPlanos from "@/pages/FPPlanos";
import FPAssistenteIA from "@/pages/FPAssistenteIA";
import FPMinhasAvaliacoes from "@/pages/FPMinhasAvaliacoes";
import CookieBanner from "@/components/CookieBanner";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import LiveHelpWidget from "@/components/LiveHelpWidget";
import { useRouteBeacon } from "@/hooks/useRouteBeacon";

const Protected = ({ children, role }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-slate-500">Carregando…</div>;
  if (!user) return <Navigate to="/entrar" state={{ from: location }} replace />;
  // Bloqueia acesso a qualquer área até definir a senha pessoal.
  if (user.requires_password_change && location.pathname !== "/trocar-senha") {
    return <Navigate to="/trocar-senha" replace />;
  }
  // Admin sempre pode acessar rotas de consultor (RBAC hierárquico).
  if (role && user.role !== role && !(role === "consultor" && user.role === "admin")) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-right" richColors />
          <AppRoutes />
          <CookieBanner />
          <InstallAppPrompt />
          <LiveHelpWidget />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

const AppRoutes = () => {
  const location = useLocation();
  useRouteBeacon(); // rastreia pageviews no backend próprio (RAF analytics)
  // Toda navegação de rota volta o scroll ao topo. Sem isso, ao clicar num
  // link a partir do rodapé de outra página (ex.: Sala de Guerra a partir
  // do /admin com scroll no final), a página nova abria já rolada e o
  // usuário achava que o conteúdo estava sumido.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  // Emergent Google Auth callback — session_id arrives as URL fragment
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Embed mode for public stats (no header/footer)
  const params = new URLSearchParams(location.search);
  const embed = params.get("embed") === "1" && location.pathname === "/estatisticas";
  if (embed) {
    return <div className="bg-white"><PublicStats /></div>;
  }
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/atendimento" element={<Atendimento />} />
        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
        <Route path="/politicas/consulta" element={<PoliciesConsultation />} />
        <Route path="/consultor/central-atendimento" element={<Protected role="consultor"><ConsultorCentralAtendimento /></Protected>} />
        <Route path="/termos-de-uso" element={<TermosUso />} />
        <Route path="/termo-de-confidencialidade" element={<TermoConfidencialidade />} />
        <Route path="/politica-de-cookies" element={<PoliticaCookies />} />
        <Route path="/meus-dados" element={<MeusDados />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/entrar" element={<Auth mode="login" />} />
        <Route path="/cadastro" element={<Auth mode="register" />} />
        <Route path="/seja-consultor" element={<ApplyConsultor />} />
        <Route path="/seja-parceiro" element={<ClinicApply />} />
        <Route path="/seja-profissional" element={<ProfessionalApply />} />
        <Route path="/profissional" element={<Protected role="profissional"><ProfessionalDashboard /></Protected>} />
        <Route path="/esqueci-minha-senha" element={<ForgotPassword />} />
        <Route path="/redefinir-senha" element={<ResetPassword />} />
        <Route path="/trocar-senha" element={<Protected><ForcePasswordChange /></Protected>} />
        <Route path="/estatisticas" element={<PublicStats />} />
        <Route path="/acompanhar" element={<PublicLookup />} />
        <Route path="/verificar/:certNum" element={<PublicVerify />} />
        <Route path="/consultores/:userId" element={<PublicConsultor />} />
        <Route path="/admin/sala-de-guerra" element={<Protected role="admin"><AdminWarRoom /></Protected>} />
        <Route path="/unidades" element={<PublicClinics />} />
        <Route path="/unidades/:clinicId" element={<PublicClinicDetail />} />
        <Route path="/profissionais" element={<PublicProfessionals />} />
        <Route path="/profissionais/:id" element={<PublicProfessionalDetail />} />
        <Route path="/pagamento/sucesso" element={<BookingSuccess />} />
        <Route path="/pagamento/cancelado" element={<BookingCancel />} />
        <Route path="/consulta/:bookingId" element={<Protected><ConsultationRoom /></Protected>} />
        <Route path="/ranking" element={<PartnerRanking />} />
        <Route path="/impacto" element={<Impact />} />
        <Route path="/perfil" element={<Protected><UserProfile /></Protected>} />
        <Route path="/clinica/perfil" element={<Protected role="clinica"><ClinicProfileEdit /></Protected>} />
        <Route path="/admin/clinicas/:clinicId" element={<Protected role="admin"><ClinicProfileEdit /></Protected>} />
        <Route path="/seguranca" element={<Protected><TwoFactor /></Protected>} />
        <Route path="/pedir-ajuda" element={<Protected><RequestHelp /></Protected>} />
        <Route path="/triagem/:caseId" element={<Protected><Triage /></Protected>} />
        <Route path="/caso/:caseId" element={<Protected><CaseView /></Protected>} />
        <Route path="/plano-familiar/:caseId" element={<Protected><FamilyPlan /></Protected>} />
        <Route path="/proposta/:caseId" element={<Protected><Proposal /></Protected>} />
        <Route path="/meus-atendimentos" element={<Protected><MyCases /></Protected>} />
        <Route path="/clinicas" element={<Protected><Clinics /></Protected>} />
        <Route path="/consultor" element={<Protected role="consultor"><ConsultantDashboard /></Protected>} />
        <Route path="/consultor/filho-presente" element={<Protected role="consultor"><ConsultantFPInbox /></Protected>} />
        <Route path="/consultor/caso/:caseId" element={<Protected role="consultor"><ConsultantCase /></Protected>} />
        <Route path="/consultor/auditoria" element={<Protected role="consultor"><AuditLog /></Protected>} />
        <Route path="/consultor/motivos" element={<Protected role="consultor"><CloseReasonsAdmin /></Protected>} />
        <Route path="/clinica" element={<Protected role="clinica"><ClinicDashboard /></Protected>} />
        <Route path="/clinica/caso/:caseId" element={<Protected role="clinica"><ClinicCase /></Protected>} />
        <Route path="/admin" element={<Protected role="admin"><AdminDashboard /></Protected>} />
        <Route path="/admin/central-atendimento" element={<Protected role="admin"><AdminCentralAtendimento /></Protected>} />
        <Route path="/admin/escala" element={<Protected role="admin"><PlantaoSchedule /></Protected>} />
        <Route path="/admin/blog" element={<Protected role="admin"><AdminBlog /></Protected>} />
        <Route path="/admin/lgpd" element={<Protected role="admin"><AdminLgpd /></Protected>} />
        <Route path="/admin/ajuda" element={<Protected role="admin"><AdminAjuda /></Protected>} />
        <Route path="/blog" element={<PublicBlog />} />
        <Route path="/blog/salvos" element={<PublicBlogSaved />} />
        <Route path="/blog/:slug" element={<PublicBlogPost />} />
        {/* Filho Presente */}
        <Route path="/filho-presente" element={<FilhoPresenteHome />} />
        <Route path="/filho-presente/profissionais" element={<FPProfessionals />} />
        <Route path="/filho-presente/profissionais/:id" element={<FPProfessionalDetail />} />
        <Route path="/filho-presente/clinicas" element={<PublicClinics />} />
        <Route path="/filho-presente/clinicas/:clinicId" element={<PublicClinicDetail />} />
        <Route path="/filho-presente/cadastre-profissional" element={<ProfessionalApply origin="fp" />} />
        <Route path="/filho-presente/cadastre-clinica" element={<ClinicApply origin="fp" />} />
        <Route path="/filho-presente/painel" element={<Protected><FilhoPresenteDashboard /></Protected>} />
        <Route path="/filho-presente/familiares" element={<Protected><FPBeneficiaryList /></Protected>} />
        <Route path="/filho-presente/familiares/novo" element={<Protected><FPBeneficiaryForm /></Protected>} />
        <Route path="/filho-presente/familiares/:id" element={<Protected><FPBeneficiaryProfile /></Protected>} />
        <Route path="/filho-presente/familiares/:id/editar" element={<Protected><FPBeneficiaryForm /></Protected>} />
        <Route path="/filho-presente/solicitar-consulta" element={<Protected><FPRequestForm /></Protected>} />
        <Route path="/filho-presente/solicitacoes" element={<Protected><FPRequestList /></Protected>} />
        <Route path="/filho-presente/solicitacoes/:id" element={<Protected><FPRequestDetail /></Protected>} />
        <Route path="/filho-presente/agenda" element={<Protected><FPAgenda /></Protected>} />
        <Route path="/filho-presente/solicitar-exame" element={<Protected><FPRequestKindForm kind="exame" /></Protected>} />
        <Route path="/filho-presente/solicitar-transporte" element={<Protected><FPRequestKindForm kind="transporte" /></Protected>} />
        <Route path="/filho-presente/solicitar-acompanhante" element={<Protected><FPRequestKindForm kind="acompanhante" /></Protected>} />
        <Route path="/filho-presente/plano-cuidados" element={<Protected><FPCarePlan /></Protected>} />
        <Route path="/filho-presente/familiares/:id/documentos" element={<Protected><FPDocuments /></Protected>} />
        <Route path="/filho-presente/profissional" element={<Protected><FPProfessionalArea /></Protected>} />
        <Route path="/filho-presente/planos" element={<FPPlanos />} />
        <Route path="/filho-presente/assistente" element={<Protected><FPAssistenteIA /></Protected>} />
        <Route path="/filho-presente/avaliacoes" element={<Protected><FPMinhasAvaliacoes /></Protected>} />
        <Route path="/admin/filho-presente" element={<Protected role="admin"><AdminFilhoPresente /></Protected>} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
};

export default App;
